import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import * as ArrayUtils from '@acceligentllc/shared/utils/array';
import type { BlockValueType } from '@acceligentllc/shared/utils/fieldReport';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { FieldReportBlockType, FieldReportBlocksTotals } from '@acceligentllc/shared/enums/fieldReportBlockType';

import type { RootState } from 'af-reducers';

import { bemBlock, bemElement } from 'af-utils/bem.util';

import { DEFAULT_EMPLOYEE_IMAGE } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import ImageTag from 'af-components/Image';
import LoadingOverlay from 'af-components/LoadingOverlay';
import Tooltip from 'af-components/Tooltip';

import * as FieldReportActions from 'af-actions/fieldReport';

import type { FieldWithIndex } from '..';
import { getFormName } from '../helpers';
import Field from './Field';
import Header from './Headers/Header';
import RepeatableField from './RepeatableField';
import type { RequestQueue } from '../../helpers';

interface OwnProps {
	fieldReportId: number;
	instanceIndex: number;
	segmentIndex: number;
	fieldReportTypeId: number;
	isPreview: boolean;
	blockId: string;
	hidden: boolean;
	showInstanceCount: boolean;
	showSegmentCount: boolean;
	secondaryCount: number;
	highlightedBlockId: Nullable<string>;
	change: (fieldName: string, value: BlockValueType) => void;
	toggleSegment: (isPrimary: boolean) => void;
	hideBlock: (fieldReportBlockId: string, isPrimary: boolean) => void;
	lock: (blockId: string) => void;
	unlock: () => void;
	forceUnlock: (blockId: string) => void;
	removable: boolean;
	focusedBlockId: Nullable<number>;
	setFieldToFocus: (ref: HTMLDivElement) => void;
	removeFocusedField: () => void;
	requestQueue: RequestQueue;
	allocatedFieldsWithIndex: Nullable<FieldWithIndex[]>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const Block: React.FC<Props> = (props) => {
	const {
		addRepeatableFields,
		blockId,
		change,
		changeCompletedStatus,
		fieldReportBlock,
		fieldReportId,
		fieldReportTypeId,
		focusedBlockId,
		forceUnlock,
		hidden,
		hideBlock,
		highlightedBlockId,
		isPreview,
		lock,
		lockData,
		removable,
		removeFocusedField,
		removeRepeatableFields,
		reportBlock,
		requestQueue,
		secondaryCount,
		setFieldToFocus,
		showInstanceCount,
		showSegmentCount,
		toggleSegment,
		unlock,
		allocatedFieldsWithIndex,
	} = props;

	const [addingBlock, setAddingBlock] = React.useState(false);
	const [deletingBlock, setDeletingBlock] = React.useState(false);
	const [collapsed, setCollapsed] = React.useState(false);
	const [completedToggled, setCompletedToggled] = React.useState(false);
	const [isMatch] = React.useState(false);
	const [isActiveMatch] = React.useState(false);

	const prevCompletedRef = React.useRef(fieldReportBlock.completed);

	const addRepeatableFieldsCallback = React.useCallback(async () => {
		setAddingBlock(true);

		await addRepeatableFields(fieldReportId, +blockId);
		setAddingBlock(false);
	}, [addRepeatableFields, blockId, fieldReportId]);

	const removeRepeatableFieldsCallback = React.useCallback(async (index: number) => {
		setDeletingBlock(true);

		await removeRepeatableFields(fieldReportId, blockId, index);
		setDeletingBlock(false);
	}, [blockId, fieldReportId, removeRepeatableFields]);

	const lockCallback = React.useCallback(() => {
		lock(blockId);
	}, [blockId, lock]);

	const toggleSecondariesCallback = React.useCallback(() => {
		toggleSegment(fieldReportBlock.isPrimary);
		setCollapsed(!collapsed);
	}, [collapsed, fieldReportBlock, toggleSegment]);

	const hideCallback = React.useCallback(() => {
		hideBlock(fieldReportBlock.id, fieldReportBlock.isMain);
	}, [fieldReportBlock.id, fieldReportBlock.isMain, hideBlock]);

	const handleCompleteCallback = React.useCallback(() => {
		setCompletedToggled(true);
		requestQueue.add(() => changeCompletedStatus(fieldReportId, +fieldReportBlock.id, !fieldReportBlock.completed));

		if (focusedBlockId) {
			removeFocusedField();
		}
	}, [changeCompletedStatus, fieldReportBlock, fieldReportId, focusedBlockId, removeFocusedField, requestQueue]);

	React.useEffect(() => {
		if (completedToggled && fieldReportBlock.completed !== prevCompletedRef.current) {
			setCompletedToggled(false);
		}
		prevCompletedRef.current = fieldReportBlock.completed;
	}, [completedToggled, fieldReportBlock.completed]);

	const allocatedFieldIds = React.useMemo(() => {
		if (!allocatedFieldsWithIndex) {
			return null;
		}

		return allocatedFieldsWithIndex.map((f) => f.fieldReportBlockFieldId);
	}, [allocatedFieldsWithIndex]);

	const renderHeader = () => {
		return (
			<Header
				blockId={blockId}
				fieldReportId={fieldReportId}
				fieldReportTypeId={fieldReportTypeId}
				forceUnlock={forceUnlock}
				hidden={hidden}
				hide={hideCallback}
				highlighted={highlightedBlockId === blockId}
				isPreview={isPreview}
				locked={!!lockData}
				removable={removable}
				secondaryCount={secondaryCount}
				showInstanceCount={showInstanceCount}
				showSegmentCount={showSegmentCount}
				unlock={unlock}
			/>
		);
	};

	const renderField = (fieldId: string, key: number) => {
		if (allocatedFieldIds?.length && !allocatedFieldIds.includes(+fieldId)) {
			return null;
		}

		return (
			<Field
				blockId={blockId}
				change={change}
				disabled={!!lockData}
				fieldId={fieldId}
				fieldName={getFormName(fieldId)}
				fieldReportId={fieldReportId}
				fieldReportTypeId={fieldReportTypeId}
				focusedBlockId={focusedBlockId}
				isPreview={isPreview}
				key={key}
				onFocus={lockCallback}
				removeFocusedField={removeFocusedField}
				requestQueue={requestQueue}
				setFieldToFocus={setFieldToFocus}
			/>
		);
	};

	const renderFields = (index: number) => {
		const { repeatingCount, fields, isJustifiable } = fieldReportBlock;

		const filteredFields = allocatedFieldIds?.length
			? fields.filter((field) => allocatedFieldIds.includes(+field))
			: fields;

		return (
			<RepeatableField
				allocatedFieldsWithIndex={allocatedFieldsWithIndex}
				blockId={blockId}
				change={change}
				disabled={!!lockData}
				fieldReportId={fieldReportId}
				fieldReportTypeId={fieldReportTypeId}
				fields={filteredFields}
				focusedBlockId={focusedBlockId}
				index={index}
				isJustifiable={isJustifiable}
				isPreview={isPreview}
				key={index}
				onFocus={lockCallback}
				remove={removeRepeatableFieldsCallback}
				removeFocusedField={removeFocusedField}
				repeatingCount={repeatingCount}
				requestQueue={requestQueue}
				setFieldToFocus={setFieldToFocus}
			/>
		);
	};

	const renderHiddenBlock = () => {
		const className = bemElement('field-report-block', 'body', { hidden: true });

		return (
			<div className={className} onClick={hideCallback}>
				<span>Click To Show Row Data.</span>
			</div>
		);
	};

	const renderBody = () => {
		const { repeatingCount, fields, isJustifiable } = fieldReportBlock;
		const { isRepeating } = reportBlock;

		if (hidden && !fieldReportBlock.isMain) {
			return renderHiddenBlock();
		}

		const disabled = !!lockData;
		const className = bemElement('field-report-block', 'body', { repeating: isRepeating });
		const fieldWrapperClassName = bemElement('field-report-block', 'field-array', { justifiable: isJustifiable, preview: isPreview });

		return (
			<div className={className}>
				<div className="field-report-block__block">
					{isRepeating ?
						ArrayUtils.range(0, repeatingCount).map(renderFields)
						:
						<div className={fieldWrapperClassName}>{fields.map(renderField)}</div>
					}
				</div>
				{(isRepeating && !disabled && !isPreview) &&
					<Button
						icon="ActionPlusSmall"
						label="Add"
						onClick={addRepeatableFieldsCallback}
						style="link"
					/>
				}
			</div>
		);
	};

	const renderExpander = () => {
		const { isPrimary } = fieldReportBlock;

		if (!fieldReportBlock.isMain || fieldReportBlock.type !== FieldReportBlockType.BLOCK || (hidden && !fieldReportBlock.isMain)) {
			return null;
		}

		const className = bemElement('field-report-block', 'expander', {
			primary: isPrimary,
			highlighted: highlightedBlockId === blockId,
			locked: !!lockData,
			active: isActiveMatch,
		});
		return (
			<div className={className}>
				<Button
					icon={collapsed ? 'CaretDown' : 'CaretUp'}
					onClick={toggleSecondariesCallback}
					style="secondary"
					tooltip={`${collapsed ? 'Hide' : 'Show'} Segment`}
				/>
			</div>
		);
	};

	const renderFooter = () => {
		const { hasCompletionStatus } = reportBlock;
		const { completed } = fieldReportBlock;

		if (!hasCompletionStatus || (hidden && !fieldReportBlock.isMain)) {
			return null;
		}

		const isChecked = completedToggled ? !completed : !!completed;

		return (
			<div className="field-report-block__footer">
				<Checkbox
					handleChange={handleCompleteCallback}
					isChecked={isChecked}
					isDisabled={!!lockData || isPreview}
					label="Completed"
				/>
			</div>
		);
	};

	const renderLockMessage = () => {
		const icon = lockData.isWeb ? 'icon-laptop' : 'icon-smartphone';
		return (
			<div className="lock-info">
				<div className="lock-info__title">
					<span>NOW EDITING</span>
					<span className={icon} />
				</div>
				<div className="lock-info__user">
					<ImageTag
						className="lock-info__user__image"
						fallbackSrc={DEFAULT_EMPLOYEE_IMAGE}
						minSize={BlobStorageImageSizeContainer.SIZE_50X50}
						src={lockData.imageUrl}
						tryOriginal={true}
						tryRoot={true}
					/>
					<div>
						<div className="lock-info__user__name">{lockData.fullName}</div>
						<div className="lock-info__user__role">{lockData.wageRateClassification}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderBlock = () => {
		if (!fieldReportBlock) {
			return null;
		}
		const { isPrimary } = fieldReportBlock;
		const { hasCompletionStatus } = reportBlock;

		const isPrimaryBlock = isPrimary || FieldReportBlocksTotals.includes(fieldReportBlock.type);

		const className = bemBlock('field-report-block', {
			main: fieldReportBlock.isMain,
			primary: isPrimaryBlock,
			secondary: !isPrimaryBlock,
			completable: hasCompletionStatus,
			locked: !!lockData,
			highlighted: highlightedBlockId === blockId,
			match: isMatch,
			active: isActiveMatch,
			'primary-total-block': fieldReportBlock.type === FieldReportBlockType.UPPER_TOTAL,
			'secondary-total-block': fieldReportBlock.type === FieldReportBlockType.LOWER_TOTAL,
		});

		return (
			<>
				<div className={className} id={blockId}>
					{renderHeader()}
					{renderExpander()}
					{renderBody()}
					{renderFooter()}
				</div>
				<LoadingOverlay label="Adding Block" show={addingBlock} />
				<LoadingOverlay label="Deleting Block" show={deletingBlock} />
			</>
		);
	};

	return !lockData ?
		renderBlock()
		:
		(
			<Tooltip
				className={bemBlock('lock-info-popover', { main: fieldReportBlock.isMain })}
				message={renderLockMessage()}
				useOverlayTrigger={false}
			>
				{renderBlock()}
			</Tooltip>
		);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { fieldReport: { fieldReport, locks, activeSearchItemIndex } } = state;
	const { blockId } = ownProps;

	const fieldReportBlock = fieldReport?.fieldReportBlockMap?.[blockId];
	if (!fieldReportBlock) {
		throw new Error('fieldReportBlock not found');
	}

	return {
		fieldReportBlock,
		reportBlock: fieldReport?.blockMap?.[fieldReportBlock?.reportBlockId],
		lockData: locks?.[blockId],
		activeSearchItemIndex,
	};
}

function mapDispatchToProps() {
	return {
		changeCompletedStatus: FieldReportActions.updateFieldReportBlockCompletedStatus,
		addRepeatableFields: FieldReportActions.addRepeatableFields,
		removeRepeatableFields: FieldReportActions.removeRepeatableFields,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(Block);
